import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { usePendingAction } from '@/composables/usePendingAction'
import { i18n } from '@/i18n'
import { isLoaded } from '@/types/load-state'
import type { ContentItem } from '@/types/content'
import type { PieceWriteAction } from '@/lib/content/piece-actions'
import { useChatStore } from './chat'
import { useConnectionsStore } from './connections'
import { useContentStore } from './content'
import { useDesignStore } from './design'
import { useDialogStore } from './dialog'
import { useToastStore } from './toast'

/** The post actions whose in-flight state the post panel shows. */
export type PostAction = PieceWriteAction | 'designMedia'

/** The one post panel the app shows in the dock, and every action it can take on a post. */
export const usePostPanelStore = defineStore('post-panel', () => {
  const content = useContentStore()
  const connections = useConnectionsStore()
  const dialog = useDialogStore()
  const design = useDesignStore()
  const chat = useChatStore()
  const pending = usePendingAction()
  const t = i18n.global.t

  /** The post the panel shows, or `null` when it is closed. */
  const postId = ref<string | null>(null)

  /** The post whose date control should take focus once the panel shows it, or `null`. */
  const dateFocusPostId = ref<string | null>(null)

  const removingPostIds = new Set<string>()

  const posts = computed<ContentItem[]>(() => (isLoaded(content.posts) ? content.posts.value : []))

  /** The open post, once the posts feed holds it. */
  const post = computed<ContentItem | null>(
    () => posts.value.find((entry) => entry.id === postId.value) ?? null,
  )

  /** False only when the LinkedIn account is known to be missing or broken. */
  const linkedInConnected = computed<boolean>(() => {
    const account = connections.linkedin.account
    if (!isLoaded(account)) return true
    return account.value?.status === 'connected'
  })

  watch(posts, (list) => {
    if (removingPostIds.size === 0) return
    const ids = new Set(list.map((entry) => entry.id))
    for (const id of removingPostIds) {
      if (ids.has(id)) continue
      pending.finish(`remove:${id}`)
      removingPostIds.delete(id)
      if (postId.value === id) postId.value = null
    }
  })

  function open(id: string): void {
    postId.value = id
  }

  /** Opens a post with its date control asking for focus. */
  function openAtDate(id: string): void {
    postId.value = id
    dateFocusPostId.value = id
  }

  function dateFocused(): void {
    dateFocusPostId.value = null
  }

  function close(): void {
    postId.value = null
    dateFocusPostId.value = null
  }

  function isPending(action: PostAction, id: string): boolean {
    return pending.isPending(`${action}:${id}`)
  }

  function refuseWhenDisconnected(): boolean {
    if (linkedInConnected.value) return false
    useToastStore().error(t('calendar.toast.reconnectFirst'))
    return true
  }

  /** Schedules a draft at `publishAt` (an ISO datetime) when given, else at its own date or the next open cadence slot. */
  async function schedule(id: string, publishAt?: string): Promise<boolean> {
    if (refuseWhenDisconnected()) return false
    return (
      (await pending.run(`schedule:${id}`, () =>
        content.schedulePost(publishAt ? { postId: id, publishAt } : { postId: id }),
      )) === true
    )
  }

  /** Sends a failed post again. */
  async function retryPublish(id: string): Promise<boolean> {
    if (refuseWhenDisconnected()) return false
    return (
      (await pending.run(`retryPublish:${id}`, () => content.schedulePost({ postId: id }))) === true
    )
  }

  /** Takes a post back to draft; its date stays. */
  async function unschedule(id: string): Promise<boolean> {
    return (
      (await pending.run(`unschedule:${id}`, () => content.cancelSchedule({ postId: id }))) === true
    )
  }

  /** Moves a post's date: a booked post is rescheduled, a draft only gets the new date. */
  async function reschedule(id: string, publishAt: string): Promise<void> {
    const status = posts.value.find((entry) => entry.id === id)?.status
    if (status === 'published') return
    if (status === 'scheduled' || status === 'pending-handoff') {
      await pending.run(`reschedule:${id}`, () => content.reschedulePost({ postId: id, publishAt }))
      return
    }
    await pending.run(`reschedule:${id}`, () => content.setPostPublishAt({ postId: id, publishAt }))
  }

  async function publishNow(id: string): Promise<void> {
    if (refuseWhenDisconnected()) return
    const confirmed = await dialog.confirm({
      title: t('content.publishDialog.title'),
      description: t('content.publishDialog.description'),
      confirmLabel: t('content.publishNow'),
      tone: 'destructive',
    })
    if (!confirmed) return
    await pending.run(`publishNow:${id}`, () => content.publishPostNow({ postId: id }))
  }

  /** Deletes a post; the pending flag clears on failure or once the feed drops it. */
  async function remove(id: string): Promise<void> {
    const confirmed = await dialog.confirm({
      title: t('content.deleteDialog.title'),
      description: t('content.deleteDialog.description'),
      confirmLabel: t('common.delete'),
      tone: 'destructive',
    })
    if (!confirmed) return
    const key = `remove:${id}`
    if (!pending.start(key)) return
    removingPostIds.add(id)
    const ok = await content.deletePost({ postId: id })
    if (ok) return
    removingPostIds.delete(id)
    pending.finish(key)
  }

  /** Copies a post's body into a new draft and opens that draft in the panel; returns its id. */
  async function duplicate(id: string): Promise<string | null> {
    const body = posts.value.find((entry) => entry.id === id)?.copy
    if (body === undefined) return null
    const created = await pending.run(`duplicate:${id}`, () => content.createPost({ body }))
    if (!created) return null
    postId.value = created
    return created
  }

  /** Starts the design of a post's media from its slides; the design attaches itself once made. */
  async function designMedia(id: string): Promise<boolean> {
    return (await pending.run(`designMedia:${id}`, () => design.designPostMedia(id))) === true
  }

  /** Puts a post back to one of its earlier versions. */
  async function restore(id: string, revisionId: string): Promise<void> {
    await content.restoreRevision({ postId: id, revisionId })
  }

  /** Sends a chat message about the post, with the post pinned as context. */
  function askRaldo(id: string, message: string): void {
    const entry = posts.value.find((candidate) => candidate.id === id)
    chat.askAboutPost({
      title: entry?.title || t('content.untitledPost'),
      message,
      ...(entry
        ? {
            state: [
              { key: 'status', value: entry.status },
              { key: 'body', value: entry.copy.trim() === '' ? 'empty' : 'not empty' },
            ],
          }
        : {}),
    })
    postId.value = null
  }

  return {
    postId,
    post,
    dateFocusPostId,
    linkedInConnected,
    open,
    openAtDate,
    dateFocused,
    close,
    isPending,
    schedule,
    retryPublish,
    unschedule,
    reschedule,
    publishNow,
    remove,
    duplicate,
    designMedia,
    restore,
    askRaldo,
  }
})
